import { AccountApiError, isRecord, requestAccountJson } from "./account-api";
import { MAX_SCREENER_SORTS, SCREENER_COLUMN_IDS } from "./screener-state";
import type { BuiltInViewId, SavedViewStateV1 } from "./types";

export interface SavedViewRecord {
  id: string;
  name: string;
  state: SavedViewStateV1;
  createdAt: string;
  updatedAt: string;
}

export interface SavedViewUpdate {
  name?: string;
  state?: SavedViewStateV1;
}

export const MAX_SAVED_VIEW_NAME_LENGTH = 80;

export function normalizeSavedViewName(value: string): string | null {
  const name = value.trim();
  return name.length > 0 && name.length <= MAX_SAVED_VIEW_NAME_LENGTH ? name : null;
}

const BUILT_IN_VIEW_IDS = new Set<BuiltInViewId>([
  "all-activity",
  "today",
  "action-queue",
  "informative-buys",
  "cluster-buys",
  "large-purchases",
  "unplanned-sells",
  "needs-review",
  "my-watchlist"
]);
const STATE_KEYS = [
  "version",
  "builtInViewId",
  "filters",
  "sorting",
  "density",
  "columnVisibility",
  "columnOrder",
  "columnPinning"
] as const;
const FILTER_KEYS = [
  "query",
  "filingDateFrom",
  "filingDateTo",
  "tradeDateFrom",
  "tradeDateTo",
  "signalBuckets",
  "relationshipFlags",
  "minTransactionValue",
  "maxTransactionValue",
  "planStatuses",
  "minDistinctInsiders",
  "exchanges",
  "transactionActivities",
  "transactionCodes",
  "securityStatuses",
  "minShares",
  "maxShares",
  "minPrice",
  "maxPrice",
  "minHoldingsChange",
  "maxHoldingsChange",
  "confidences",
  "amendmentStates",
  "valueQualities",
  "missingFieldFlags",
  "industryDescription",
  "marketCapBuckets",
  "minValueToMarketCapBps",
  "minFilingLagDays",
  "maxFilingLagDays",
  "accessionNumber",
  "favoritesOnly"
] as const;
const SIGNAL_BUCKETS = new Set([
  "informative_buy",
  "informative_sell",
  "uninformative_buy",
  "uninformative_sell",
  "needs_review"
]);
const RELATIONSHIP_FLAGS = new Set(["CEO", "CFO", "Director", "Officer", "10% Owner", "Other"]);
const PLAN_STATUSES = new Set(["planned", "not_planned", "unknown"]);
const ACTIVITIES = new Set(["buy", "sell", "option", "award"]);
const SECURITY_STATUSES = new Set(["common_equity", "non_common", "derivative", "unknown"]);
const CONFIDENCES = new Set(["high", "medium", "low"]);
const MARKET_CAP_BUCKETS = new Set(["micro", "small", "mid", "large", "mega", "unknown"]);
const AMENDMENT_STATES = new Set(["original", "amendment"]);
const VALUE_QUALITIES = new Set(["reported", "derived", "missing", "suspect"]);
const MISSING_FIELDS = new Set([
  "tradeDate",
  "price",
  "value",
  "holdingsBefore",
  "holdingsAfter",
  "ownerCik",
  "ownerIdentityKey",
  "filingLagDays"
]);
const SORT_FIELDS = new Set([
  "filingDate",
  "tradeDate",
  "ticker",
  "companyName",
  "insiderName",
  "value",
  "shares",
  "price",
  "ownershipChangePct",
  "materialityBps",
  "filingLagDays",
  "score",
  "confidence",
  "distinctInsiderCount"
]);
const COLUMN_ID_SET = new Set<string>(SCREENER_COLUMN_IDS);

export async function loadSavedViews(): Promise<SavedViewRecord[]> {
  const endpoint = "/api/saved-views";
  const payload = await requestSavedViews(endpoint);
  if (!isRecord(payload) || !Array.isArray(payload.views)) invalid(endpoint);
  return payload.views.map((view) => parseSavedView(view, endpoint));
}

export async function createSavedView(
  name: string,
  state: SavedViewStateV1,
  idempotencyKey: string
): Promise<SavedViewRecord> {
  const endpoint = "/api/saved-views";
  const payload = await requestSavedViews(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey
    },
    body: JSON.stringify({ name, state })
  });
  if (!isRecord(payload) || !Object.prototype.hasOwnProperty.call(payload, "view")) {
    invalid(endpoint);
  }
  return parseSavedView(payload.view, endpoint);
}

export async function updateSavedView(
  id: string,
  update: SavedViewUpdate
): Promise<SavedViewRecord> {
  const endpoint = `/api/saved-views/${encodeURIComponent(id)}`;
  const payload = await requestSavedViews(endpoint, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update)
  });
  if (!isRecord(payload) || !Object.prototype.hasOwnProperty.call(payload, "view")) {
    invalid(endpoint);
  }
  return parseSavedView(payload.view, endpoint);
}

export async function deleteSavedView(id: string): Promise<void> {
  const endpoint = `/api/saved-views/${encodeURIComponent(id)}`;
  const payload = await requestSavedViews(endpoint, { method: "DELETE" });
  if (!isRecord(payload) || payload.deleted !== true) invalid(endpoint);
}

function requestSavedViews(endpoint: string, init?: RequestInit): Promise<unknown> {
  return requestAccountJson({
    endpoint,
    init,
    unavailableCode: "saved_views_unavailable",
    httpCode: "saved_views_http",
    invalidCode: "saved_views_invalid"
  });
}

function parseSavedView(value: unknown, endpoint: string): SavedViewRecord {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    value.id === "" ||
    typeof value.name !== "string" ||
    value.name === "" ||
    typeof value.createdAt !== "string" ||
    typeof value.updatedAt !== "string" ||
    !isSavedViewState(value.state)
  ) {
    invalid(endpoint);
  }
  return {
    id: value.id,
    name: value.name,
    state: value.state,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt
  };
}

function isSavedViewState(value: unknown): value is SavedViewStateV1 {
  if (
    !hasExactKeys(value, STATE_KEYS) ||
    value.version !== 1 ||
    typeof value.builtInViewId !== "string" ||
    !BUILT_IN_VIEW_IDS.has(value.builtInViewId as BuiltInViewId) ||
    !validFilters(value.filters) ||
    !validSorting(value.sorting) ||
    (value.density !== "compact" && value.density !== "comfortable") ||
    !validVisibility(value.columnVisibility) ||
    !validColumnOrder(value.columnOrder) ||
    !validPinning(value.columnPinning)
  ) {
    return false;
  }
  return true;
}

function validFilters(value: unknown): boolean {
  if (!hasExactKeys(value, FILTER_KEYS)) return false;
  return (
    validText(value.query) &&
    validNullableDate(value.filingDateFrom) &&
    validNullableDate(value.filingDateTo) &&
    validNullableDate(value.tradeDateFrom) &&
    validNullableDate(value.tradeDateTo) &&
    validEnumArray(value.signalBuckets, SIGNAL_BUCKETS) &&
    validEnumArray(value.relationshipFlags, RELATIONSHIP_FLAGS) &&
    validNullableNumber(value.minTransactionValue) &&
    validNullableNumber(value.maxTransactionValue) &&
    validEnumArray(value.planStatuses, PLAN_STATUSES) &&
    (value.minDistinctInsiders === null ||
      (Number.isSafeInteger(value.minDistinctInsiders) &&
        (value.minDistinctInsiders as number) >= 1)) &&
    validStringArray(value.exchanges) &&
    validEnumArray(value.transactionActivities, ACTIVITIES) &&
    validStringArray(value.transactionCodes) &&
    validEnumArray(value.securityStatuses, SECURITY_STATUSES) &&
    validNullableNumber(value.minShares) &&
    validNullableNumber(value.maxShares) &&
    validNullableNumber(value.minPrice) &&
    validNullableNumber(value.maxPrice) &&
    validNullableNumber(value.minHoldingsChange) &&
    validNullableNumber(value.maxHoldingsChange) &&
    validEnumArray(value.confidences, CONFIDENCES) &&
    validEnumArray(value.amendmentStates, AMENDMENT_STATES) &&
    validEnumArray(value.valueQualities, VALUE_QUALITIES) &&
    validEnumArray(value.missingFieldFlags, MISSING_FIELDS) &&
    validText(value.industryDescription) &&
    validEnumArray(value.marketCapBuckets, MARKET_CAP_BUCKETS) &&
    validNullableNumber(value.minValueToMarketCapBps) &&
    validNullableNumber(value.minFilingLagDays) &&
    validNullableNumber(value.maxFilingLagDays) &&
    validText(value.accessionNumber) &&
    typeof value.favoritesOnly === "boolean"
  );
}

function validSorting(value: unknown): boolean {
  if (!Array.isArray(value) || value.length > MAX_SCREENER_SORTS) return false;
  const seen = new Set<string>();
  return value.every((sort) => {
    if (
      !hasExactKeys(sort, ["id", "desc"]) ||
      typeof sort.id !== "string" ||
      !SORT_FIELDS.has(sort.id) ||
      typeof sort.desc !== "boolean" ||
      seen.has(sort.id)
    ) {
      return false;
    }
    seen.add(sort.id);
    return true;
  });
}

function validVisibility(value: unknown): boolean {
  return (
    isRecord(value) &&
    Object.keys(value).length <= SCREENER_COLUMN_IDS.length &&
    Object.entries(value).every(
      ([columnId, visible]) => COLUMN_ID_SET.has(columnId) && typeof visible === "boolean"
    )
  );
}

function validColumnOrder(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.length === SCREENER_COLUMN_IDS.length &&
    new Set(value).size === SCREENER_COLUMN_IDS.length &&
    value.every((columnId) => typeof columnId === "string" && COLUMN_ID_SET.has(columnId))
  );
}

function validPinning(value: unknown): boolean {
  if (!hasExactKeys(value, ["left", "right"])) return false;
  if (!validColumnIds(value.left) || !validColumnIds(value.right)) return false;
  const left = new Set(value.left);
  return value.right.every((columnId) => !left.has(columnId));
}

function validColumnIds(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= SCREENER_COLUMN_IDS.length &&
    new Set(value).size === value.length &&
    value.every((columnId) => typeof columnId === "string" && COLUMN_ID_SET.has(columnId))
  );
}

function validEnumArray(value: unknown, allowed: ReadonlySet<string>): boolean {
  return (
    Array.isArray(value) &&
    value.length <= 64 &&
    new Set(value).size === value.length &&
    value.every((item) => typeof item === "string" && allowed.has(item))
  );
}

function validStringArray(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.length <= 64 &&
    new Set(value).size === value.length &&
    value.every((item) => typeof item === "string" && item.length > 0 && item.length <= 128)
  );
}

function validNullableDate(value: unknown): boolean {
  return value === null || (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value));
}

function validNullableNumber(value: unknown): boolean {
  return value === null || (typeof value === "number" && Number.isFinite(value));
}

function validText(value: unknown): boolean {
  return typeof value === "string" && value.length <= 256;
}

function hasExactKeys<const T extends readonly string[]>(
  value: unknown,
  keys: T
): value is Record<T[number], unknown> {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function invalid(endpoint: string): never {
  throw new AccountApiError("saved_views_invalid", "Saved views response has an invalid shape", {
    endpoint
  });
}
